import { useState, useEffect, useCallback } from 'react';
import { Shield, Plus, Trash2, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { blacklistAPI, type RiskBlacklistItem } from '../../services/api';
import type { PaginationInfo } from '../../services/api';
import { formatInBeijing } from '../../utils/time';

const TYPE_LABELS: Record<string, string> = {
  ip: 'IP',
  email: '邮箱',
  phone: '手机号',
};

const TYPE_COLORS: Record<string, string> = {
  ip: 'bg-blue-100 text-blue-700',
  email: 'bg-purple-100 text-purple-700',
  phone: 'bg-green-100 text-green-700',
};

export default function BlacklistTab() {
  const [items, setItems] = useState<RiskBlacklistItem[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo>({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(false);
  const [typeFilter, setTypeFilter] = useState('');
  const [search, setSearch] = useState('');

  // 添加表单
  const [newType, setNewType] = useState('ip');
  const [newValue, setNewValue] = useState('');
  const [newReason, setNewReason] = useState('');
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async (page = 1) => {
    setLoading(true);
    setError('');
    try {
      const data = await blacklistAPI.list({
        page,
        limit: 20,
        type: typeFilter || undefined,
        search: search.trim() || undefined,
      });
      setItems(data.data);
      setPagination(data.pagination);
    } catch (err) {
      setError(err instanceof Error ? err.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, [typeFilter, search]);

  useEffect(() => {
    load(1);
  }, [load]);

  const handleAdd = async () => {
    if (!newValue.trim()) {
      setError('值不能为空');
      return;
    }
    setAdding(true);
    setError('');
    try {
      await blacklistAPI.create({ type: newType, value: newValue.trim(), reason: newReason.trim() || undefined });
      setNewValue('');
      setNewReason('');
      load(1);
    } catch (err) {
      setError(err instanceof Error ? err.message : '添加失败');
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('确定要从黑名单中移除该条目吗？')) return;
    try {
      await blacklistAPI.remove(id);
      load(pagination.page);
    } catch (err) {
      setError(err instanceof Error ? err.message : '删除失败');
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-light-border overflow-hidden">
      {/* 标题 */}
      <div className="px-6 py-4 border-b border-light-border flex items-center gap-3">
        <div className="w-10 h-10 bg-rose-500 rounded-xl flex items-center justify-center">
          <Shield className="w-6 h-6 text-white" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-light-text">风控黑名单</h3>
          <p className="text-sm text-light-muted">拦截指定 IP、邮箱或手机号的注册请求</p>
        </div>
      </div>

      {/* 添加表单 */}
      <div className="p-4 border-b border-light-border space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-xs text-light-muted block mb-1">类型</label>
            <select
              value={newType}
              onChange={(e) => setNewType(e.target.value)}
              className="px-3 py-2 border border-light-border rounded-xl focus:outline-none focus:border-accent-blue text-sm text-light-text bg-white"
            >
              <option value="ip">IP</option>
              <option value="email">邮箱</option>
              <option value="phone">手机号</option>
            </select>
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="text-xs text-light-muted block mb-1">值</label>
            <input
              type="text"
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
              placeholder={newType === 'ip' ? '如 1.2.3.4' : newType === 'email' ? '如 user@example.com' : '如 13800138000'}
              className="w-full px-3 py-2 border border-light-border rounded-xl focus:outline-none focus:border-accent-blue text-sm text-light-text"
            />
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="text-xs text-light-muted block mb-1">原因（可选）</label>
            <input
              type="text"
              value={newReason}
              onChange={(e) => setNewReason(e.target.value)}
              placeholder="如：批量注册"
              className="w-full px-3 py-2 border border-light-border rounded-xl focus:outline-none focus:border-accent-blue text-sm text-light-text"
            />
          </div>
          <button
            onClick={handleAdd}
            disabled={adding}
            className="px-4 py-2 bg-accent-blue text-white rounded-xl hover:bg-accent-blue/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2 text-sm font-medium whitespace-nowrap"
          >
            {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            添加
          </button>
        </div>
      </div>

      {/* 筛选 */}
      <div className="p-4 border-b border-light-border flex items-center gap-3 flex-wrap">
        <select
          value={typeFilter}
          onChange={(e) => { setTypeFilter(e.target.value); }}
          className="px-3 py-2 border border-light-border rounded-xl focus:outline-none focus:border-accent-blue text-sm text-light-text bg-white"
        >
          <option value="">全部类型</option>
          <option value="ip">IP</option>
          <option value="email">邮箱</option>
          <option value="phone">手机号</option>
        </select>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="搜索值..."
          className="px-3 py-2 border border-light-border rounded-xl focus:outline-none focus:border-accent-blue text-sm text-light-text"
        />
      </div>

      {error && (
        <div className="px-4 py-2 bg-red-50 border-b border-red-100">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* 列表 */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-light-hover">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-light-text">类型</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-light-text">值</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-light-text">原因</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-light-text">创建时间</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-light-text">操作</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-light-muted">
                  <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
                  加载中...
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-light-muted">
                  暂无黑名单数据
                </td>
              </tr>
            ) : (
              items.map((item) => (
                <tr key={item.id} className="border-t border-light-border">
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-1 rounded-lg text-xs font-medium ${TYPE_COLORS[item.type] || 'bg-gray-100 text-gray-700'}`}>
                      {TYPE_LABELS[item.type] || item.type}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-light-text font-mono">{item.value}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-light-muted">{item.reason || '-'}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-light-muted">
                      {formatInBeijing(item.createdAt, { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => handleDelete(item.id)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                      <span>移除</span>
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* 分页 */}
      {pagination.totalPages > 1 && (
        <div className="p-4 border-t border-light-border flex items-center justify-between">
          <span className="text-sm text-light-muted">
            共 {pagination.total} 条，第 {pagination.page}/{pagination.totalPages} 页
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => load(pagination.page - 1)}
              disabled={pagination.page <= 1}
              className="p-2 border border-light-border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:border-accent-blue transition-colors"
            >
              <ChevronLeft className="w-4 h-4 text-light-text" />
            </button>
            <button
              onClick={() => load(pagination.page + 1)}
              disabled={pagination.page >= pagination.totalPages}
              className="p-2 border border-light-border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:border-accent-blue transition-colors"
            >
              <ChevronRight className="w-4 h-4 text-light-text" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
